import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { PolaroidFeed } from '@/components/PolaroidFeed';
import { PhotoDetail } from '@/components/PhotoDetail';
import { DateDoodle } from '@/components/DateDoodle';
import { useHashtagPictures } from '@/hooks/useHashtagPictures';
import { cn } from '@/lib/utils';
import NotFound from './NotFound';

export default function HashtagFeed() {
  const { tag } = useParams<{ tag: string }>();
  const { hasBgImage } = useDittoThemeContext();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useHashtagPictures(tag);
  const [selectedPhoto, setSelectedPhoto] = useState<NostrEvent | null>(null);

  // Flatten pages and deduplicate by event ID
  const pictures = useMemo(() => {
    const seen = new Set<string>();
    return (data?.pages.flat() ?? []).filter((event) => {
      if (seen.has(event.id)) return false;
      seen.add(event.id);
      return true;
    });
  }, [data?.pages]);

  const pageCount = data?.pages.length ?? 0;

  useSeoMeta({
    title: tag ? `#${tag} — Polaroids` : 'Polaroids',
    description: tag ? `Photos tagged #${tag} on Polaroids` : 'A digital scrapbook for your Nostr photos.',
  });

  if (!tag) {
    return <NotFound />;
  }

  return (
    <AppShell>
      {/* Hashtag header */}
      <div className="flex flex-col items-center text-center pt-6 pb-2">
        <div className={cn('flex items-center gap-2 px-3 py-1.5', hasBgImage ? 'paper-strip' : 'paper-strip-pastel')}>
          <DateDoodle seed={tag} size={28} />
          <h1
            className="font-hand font-bold text-pol-text"
            style={{
              fontSize: 'clamp(2rem, 7vw, 3rem)',
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            #{tag}
          </h1>
          <DateDoodle seed={tag + 'end'} size={24} />
        </div>
      </div>

      <PolaroidFeed
        pictures={pictures}
        isLoading={isLoading}
        hasNextPage={!!hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        pageCount={pageCount}
        onPhotoClick={setSelectedPhoto}
        excludeTag={tag}
        emptyState={
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="bg-polaroid-white rounded-[3px] p-4 pb-12 border-2 border-dashed border-cream-dark shadow-sm w-44 mx-auto" style={{ transform: 'rotate(-2deg)' }}>
              <div className="aspect-square flex items-center justify-center bg-cream-dark/50 rounded-sm">
                <span className="text-3xl">📷</span>
              </div>
              <p className="font-hand text-pol-text-secondary text-center mt-2">No #{tag} photos yet</p>
            </div>
          </div>
        }
      />

      {/* Photo detail overlay */}
      {selectedPhoto && (
        <PhotoDetail
          event={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </AppShell>
  );
}
